import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Input from '@components/common/Input';
import CurrencyInput from '@components/common/CurrencyInput';
import DatePicker from '@components/common/DatePicker';
import Select from '@components/common/Select';
import Button from '@components/common/Button';
import { useAccounts } from '@hooks/useAccounts';
import { useCategories } from '@hooks/useCategories';
import { formatCurrency } from '@utils/formatters';

const installmentSchema = z.object({
  description: z.string().min(2, 'Descrição obrigatória'),
  totalAmount: z.number().positive('Valor deve ser positivo').optional(),
  installmentAmount: z.number().positive('Valor da parcela deve ser positivo').optional(),
  totalInstallments: z.coerce.number().int().min(2, 'Mínimo 2 parcelas').max(72, 'Máximo 72 parcelas'),
  accountId: z.string().min(1, 'Conta obrigatória'),
  categoryId: z.string().min(1, 'Categoria obrigatória'),
  startDate: z.string().min(1, 'Data da primeira parcela obrigatória'),
  dayOfMonth: z.coerce.number().int().min(1).max(31),
  notes: z.string().optional(),
});

const InstallmentForm = ({ installment, onSubmit, onCancel, isLoading }) => {
  const [inputMode, setInputMode] = useState('total'); // 'total' | 'installment'
  const { accounts } = useAccounts();
  const { expenseCategories } = useCategories();

  const {
    register,
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(installmentSchema),
    defaultValues: {
      description: '',
      totalAmount: '',
      installmentAmount: '',
      totalInstallments: 12,
      accountId: '',
      categoryId: '',
      startDate: new Date().toISOString().split('T')[0],
      dayOfMonth: new Date().getDate(),
      notes: '',
    },
  });

  const totalAmount = watch('totalAmount');
  const installmentAmountField = watch('installmentAmount');
  const totalInstallments = watch('totalInstallments');

  const previewInstallment = inputMode === 'total' && totalAmount && totalInstallments
    ? totalAmount / totalInstallments
    : 0;
  const previewTotal = inputMode === 'installment' && installmentAmountField && totalInstallments
    ? installmentAmountField * totalInstallments
    : 0;

  useEffect(() => {
    if (installment) {
      reset({
        description: installment.description,
        totalAmount: installment.totalAmount,
        installmentAmount: '',
        totalInstallments: installment.totalInstallments,
        accountId: installment.accountId,
        categoryId: installment.categoryId,
        startDate: installment.startDate,
        dayOfMonth: installment.dayOfMonth,
        notes: installment.notes || '',
      });
    }
  }, [installment, reset]);

  const handleModeChange = (mode) => {
    setInputMode(mode);
    setValue('totalAmount', '');
    setValue('installmentAmount', '');
  };

  const handleFormSubmit = (data) => {
    if (inputMode === 'installment') {
      data.totalAmount = data.installmentAmount * data.totalInstallments;
    }
    onSubmit(data);
  };

  const accountOptions = accounts.map(acc => ({
    value: acc.accountId,
    label: `${acc.icon} ${acc.name}`,
  }));

  const categoryOptions = expenseCategories.map(cat => ({
    value: cat.categoryId,
    label: `${cat.icon} ${cat.name}`,
  }));

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <Input
        label="Descrição"
        placeholder="Ex: iPhone 15, Sofá, TV..."
        error={errors.description?.message}
        {...register('description')}
      />

      {/* Toggle de modo de entrada */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Informar valor por
        </label>
        <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <button
            type="button"
            onClick={() => handleModeChange('total')}
            className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
              inputMode === 'total'
                ? 'bg-primary-600 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            Valor Total
          </button>
          <button
            type="button"
            onClick={() => handleModeChange('installment')}
            className={`flex-1 px-4 py-2 text-sm font-medium transition-colors border-l border-gray-200 dark:border-gray-700 ${
              inputMode === 'installment'
                ? 'bg-primary-600 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            Valor da Parcela
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {inputMode === 'total' ? (
          <Controller
            name="totalAmount"
            control={control}
            render={({ field }) => (
              <CurrencyInput
                label="Valor Total"
                value={field.value}
                onValueChange={(values) => field.onChange(values.floatValue ?? '')}
                onBlur={field.onBlur}
                error={errors.totalAmount?.message}
              />
            )}
          />
        ) : (
          <Controller
            name="installmentAmount"
            control={control}
            render={({ field }) => (
              <CurrencyInput
                label="Valor da Parcela"
                value={field.value}
                onValueChange={(values) => field.onChange(values.floatValue ?? '')}
                onBlur={field.onBlur}
                error={errors.installmentAmount?.message}
              />
            )}
          />
        )}

        <Input
          label="Número de Parcelas"
          type="number"
          min="2"
          max="72"
          error={errors.totalInstallments?.message}
          {...register('totalInstallments')}
        />
      </div>

      {/* Preview */}
      {inputMode === 'total' && previewInstallment > 0 && (
        <div className="p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
          <p className="text-sm text-primary-600 dark:text-primary-400">
            Valor de cada parcela:
          </p>
          <p className="text-2xl font-bold text-primary-700 dark:text-primary-300">
            {formatCurrency(previewInstallment)}
          </p>
          <p className="text-xs text-primary-500 mt-1">
            {totalInstallments}x de {formatCurrency(previewInstallment)}
          </p>
        </div>
      )}

      {inputMode === 'installment' && previewTotal > 0 && (
        <div className="p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
          <p className="text-sm text-primary-600 dark:text-primary-400">
            Valor total calculado:
          </p>
          <p className="text-2xl font-bold text-primary-700 dark:text-primary-300">
            {formatCurrency(previewTotal)}
          </p>
          <p className="text-xs text-primary-500 mt-1">
            {totalInstallments}x de {formatCurrency(installmentAmountField)}
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <DatePicker
          label="Data da 1ª Parcela"
          error={errors.startDate?.message}
          {...register('startDate')}
        />

        <Input
          label="Dia do Vencimento"
          type="number"
          min="1"
          max="31"
          error={errors.dayOfMonth?.message}
          {...register('dayOfMonth')}
        />
      </div>

      <Select
        label="Conta"
        options={accountOptions}
        placeholder="Selecione uma conta"
        error={errors.accountId?.message}
        {...register('accountId')}
      />

      <Select
        label="Categoria"
        options={categoryOptions}
        placeholder="Selecione uma categoria"
        error={errors.categoryId?.message}
        {...register('categoryId')}
      />

      <Input
        label="Observações (opcional)"
        placeholder="Adicione uma nota..."
        {...register('notes')}
      />

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" isLoading={isLoading}>
          {installment ? 'Salvar' : 'Criar Parcelamento'}
        </Button>
      </div>
    </form>
  );
};

export default InstallmentForm;
